import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { useTenantAuthStore } from './store/tenantAuthStore';
import { authService } from './services/auth';
import { LoginModal } from './components/auth';
import { GlobalLoadingProvider } from './contexts/GlobalLoadingContext';
import { GlobalLoadingOverlay } from './components/GlobalLoadingOverlay';
import { Toaster } from './components/shadcn/toaster';
import { PageLoader } from './components/ui/PageLoader';
import { queryClient } from './lib/queryClient';
import { usePageLoading } from './hooks/usePageLoading';

// ✅ OPTIMIZATION: Lazy load all page components for code splitting
// This reduces initial bundle size from 372KB to ~100KB gzipped
const HomePage = lazy(() => import('./pages/HomePage'));
const ResourcesPage = lazy(() => import('./pages/ResourcesPage'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const ChatRedesign = lazy(() => import('./pages/ChatRedesign'));
const KnowledgeBase = lazy(() => import('./pages/KnowledgeBase'));
const Analytics = lazy(() => import('./pages/Analytics'));
const UserManagement = lazy(() => import('./pages/UserManagement'));
const KnowledgeBaseAccess = lazy(() => import('./pages/KnowledgeBaseAccess'));
const TenantConfiguration = lazy(() => import('./pages/TenantConfiguration'));
const EvalConsole = lazy(() => import('./pages/EvalConsole'));
const Health = lazy(() => import('./pages/platform/Health'));
const UnderConstruction = lazy(() => import('./pages/UnderConstruction'));
const Forbidden = lazy(() => import('./pages/Forbidden'));

// Import RoleProtectedRoute
import { RoleProtectedRoute } from './components/auth/RoleProtectedRoute';
import { ROUTES } from '@/config/routes';

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, setShowLoginModal } = useTenantAuthStore();

  // Use useEffect to avoid calling setState during render
  React.useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      setShowLoginModal(true);
    }
  }, [isLoading, isAuthenticated, setShowLoginModal]);

  // While the session/profile is being restored (sign-in, page refresh) show the same global overlay
  // every page uses, instead of a bare "Loading..." text.
  usePageLoading(isLoading, { message: 'Signing you in…' });

  if (isLoading) {
    return null;
  }

  if (!isAuthenticated) {
    // Redirect to home page
    return <Navigate to={ROUTES.HOME} replace />;
  }

  return <>{children}</>;
};

function App() {
  const { showLoginModal, setShowLoginModal } = useTenantAuthStore();

  useEffect(() => {
    let mounted = true;

    // Listen to auth state changes for session management
    const subscription = authService.onAuthStateChange((event, session) => {
      console.log('[App] Auth state changed:', event, 'has session:', !!session);

      if (event === 'SIGNED_OUT') {
        // User signed out - just update the state, don't call logout() again
        console.log('[App] SIGNED_OUT event - updating state only');
        // Also covers sign-out from another tab or an expired session: no cached data survives.
        queryClient.clear();
        useTenantAuthStore.setState({
          user: null,
          isAuthenticated: false,
          showLoginModal: false,
          error: null
        });
      } else if (event === 'TOKEN_REFRESHED') {
        // Token refreshed - session is already updated by Supabase
        console.log('[App] Session token refreshed');
      } else if (event === 'SIGNED_IN' && session) {
        // SIGNED_IN event fires after successful login
        // loginWithTenant already set the authenticated state
        console.log('[App] SIGNED_IN event - auth state already managed');
      } else if (event === 'INITIAL_SESSION' && session && mounted) {
        // Initial session event with valid session - restore auth state on page refresh
        // CRITICAL: Only restore if session exists, ignore INITIAL_SESSION without session
        console.log('[App] INITIAL_SESSION with session - restoring user profile');
        useTenantAuthStore.getState().checkAuth();
      } else if (event === 'INITIAL_SESSION' && !session) {
        // INITIAL_SESSION without session means user is not logged in
        console.log('[App] INITIAL_SESSION without session - user not logged in');
        useTenantAuthStore.setState({ isLoading: false });
      }
    });

    // Cleanup subscription on unmount
    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []); // Empty deps - only run once on mount

  return (
    <QueryClientProvider client={queryClient}>
      <GlobalLoadingProvider>
        <BrowserRouter>
        {/* Global Loading Overlay — driven per-page via usePageLoading(),
            so it stays up until each page's data has actually loaded. */}
        <GlobalLoadingOverlay />

        {/* Toast Notifications */}
        <Toaster />

        {/* Global Login Modal - Shows on session timeout/401 errors */}
        <LoginModal
          open={showLoginModal}
          onOpenChange={setShowLoginModal}
        />

        {/* ✅ Suspense wrapper for lazy-loaded routes.
            PageLoader covers the chunk-download gap after the navigation
            overlay's fixed timer ends — never a blank screen. */}
        <Suspense fallback={<PageLoader />}>
          <Routes>
          {/* Public Routes */}
          <Route path={ROUTES.HOME} element={<HomePage />} />
          <Route path={ROUTES.RESOURCES} element={<ResourcesPage />} />

          {/* Protected Routes with Role-Based Access Control */}
          <Route
            path={ROUTES.DASHBOARD}
            element={
              <ProtectedRoute>
                <RoleProtectedRoute pagePath={ROUTES.DASHBOARD}>
                  <Dashboard />
                </RoleProtectedRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.CHAT}
            element={
              <ProtectedRoute>
                <RoleProtectedRoute pagePath={ROUTES.CHAT}>
                  <ChatRedesign />
                </RoleProtectedRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.KNOWLEDGE_BASE}
            element={
              <ProtectedRoute>
                <RoleProtectedRoute pagePath={ROUTES.KNOWLEDGE_BASE}>
                  <KnowledgeBase />
                </RoleProtectedRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.ANALYTICS}
            element={
              <ProtectedRoute>
                <RoleProtectedRoute pagePath={ROUTES.ANALYTICS}>
                  <Analytics />
                </RoleProtectedRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.USER_MANAGEMENT}
            element={
              <ProtectedRoute>
                <RoleProtectedRoute pagePath={ROUTES.USER_MANAGEMENT}>
                  <UserManagement />
                </RoleProtectedRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.KB_ACCESS}
            element={
              <ProtectedRoute>
                <RoleProtectedRoute pagePath={ROUTES.KB_ACCESS}>
                  <KnowledgeBaseAccess />
                </RoleProtectedRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.TENANT_CONFIGURATION}
            element={
              <ProtectedRoute>
                <RoleProtectedRoute pagePath={ROUTES.TENANT_CONFIGURATION}>
                  <TenantConfiguration />
                </RoleProtectedRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.EVAL}
            element={
              <ProtectedRoute>
                <RoleProtectedRoute pagePath={ROUTES.EVAL}>
                  <EvalConsole />
                </RoleProtectedRoute>
              </ProtectedRoute>
            }
          />
          {/* Platform screen: Health ([BY4]). The Deployment page was merged into Tenant Configuration
              ([UX1]) — its old address redirects there. */}
          <Route path={ROUTES.LEGACY_DEPLOYMENT} element={<Navigate to={ROUTES.TENANT_CONFIGURATION} replace />} />
          <Route
            path={ROUTES.HEALTH}
            element={
              <ProtectedRoute>
                <RoleProtectedRoute pagePath={ROUTES.HEALTH}>
                  <Health />
                </RoleProtectedRoute>
              </ProtectedRoute>
            }
          />

          {/* Forbidden page */}
          <Route path={ROUTES.FORBIDDEN} element={<Forbidden />} />

          {/* Catch-all route for undefined paths */}
          <Route
            path="*"
            element={<UnderConstruction />}
          />
          </Routes>
        </Suspense>
        </BrowserRouter>
      </GlobalLoadingProvider>
    </QueryClientProvider>
  );
}

export default App;

